import { useMemoRef, useStyles } from '@platform/react/hook'
import { Box, Grid, Link } from '@mui/material'
import Icon from 'ui/Element/Icon'
import { getThemeColor, PlatformEvent } from 'lib/util'
import { useCallback, useEffect, useState } from 'react'
import Text from 'ui/Element/Text'
import OfflineHOC from 'platform/react/hoc/offline'
import SkeletonHOC from 'platform/react/hoc/skeleton'

const styles = (theme, { inline, onClick }) => ({
  root: {
    borderRadius: '0.5rem',
    padding: '1rem',
    height: inline ? '4.5rem' : '7.5rem', // 72px, 120px
    backgroundColor: getThemeColor(theme, 'gray.960'),
    '&:hover': {
      backgroundColor: getThemeColor(theme, 'gray.930'),
      ...onClick && { cursor: 'pointer' },
    },
  },
  ...theme.custom.cardSmall,
})

/**
 * Small Card Component
 *
 * Displays a small card that is only one row tall (the height of the icon used)
 *
 * @param {React.Ref} forwardedRef  ref to the element
 * @param {Object} events           events for the element
 * @param {Object} parentClasses    overriding styles
 * @param {Object} props            additional props
 * @returns {JSX.Element}
 */
const SmallCard = ({
  forwardedRef,
  events,
  label,
  icon,

  link = false,
  target = '_blank',

  color = 'gray.contrastText',
  textVariant = '16/bold',
  inline = false,
}) => {
  const { onClick, onOpen } = events
  const [data, setData] = useState(null)

  const classes = useStyles(styles, { inline, onClick })()

  useEffect(() => {
    (async () => {
      const result = await onOpen?.(null)

      result && setData(result)
    })()
  }, [])

  useEffect(() => {
    (data !== null || !onOpen) && events?.showSkeleton?.(false)
  }, [data])

  const handleClick = useCallback(e => onClick?.(new PlatformEvent(e, { data })), [onClick, data])

  const {
    size = 24,
    name = 'device',
    color: iconColor = 'black.main',
    background = 'background.light',
  } = icon || {}

  return (
    <Grid
      container
      ref={forwardedRef}

      {...onClick && {
        component: Box,
        onClick: handleClick,
      }}

      {...link && data && {
        component: Link,
        target,
        href: data,
      }}

      className={classes.root}

      columnGap={'1rem'}
      flexWrap={'nowrap'}
      direction={inline ? 'row' : 'column'}
      alignItems={inline ? 'center' : 'flex-start'}
      justifyContent={inline ? 'flex-start' : 'space-between'}
    >
      <Grid
        item
        component={Icon}

        size={size}
        name={name}
        shape={'circle'}
        color={iconColor}
        iconPadding={'0.5rem'}
        background={background}
      />
      <Grid
        item
        component={Text}

        maxLines={1}
        value={label}
        variant={textVariant}
        color={color}
      />
    </Grid>
  )
}

export default useMemoRef(OfflineHOC(SkeletonHOC(SmallCard)), [])
